/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { ButtonGroup, Button } from 'react-bootstrap';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { ThemeRiverChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  SingleAxisComponent,
  LegendComponent,
  ToolboxComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import {
  fetchRiskEvolution,
  RiskEvolutionData
} from 'services/hvarisk.service';
import { useAppContext } from 'providers/AppProvider';

echarts.use([
  ThemeRiverChart,
  TitleComponent,
  TooltipComponent,
  SingleAxisComponent,
  LegendComponent,
  ToolboxComponent,
  CanvasRenderer
]);

interface RiskEvolutionChartProps {
  title: string;
  dateRange: {
    datefrom: string;
    dateto: string;
  };
  campaignId?: number;
  categoryId?: number;
}

const RiskEvolutionChart: React.FC<RiskEvolutionChartProps> = ({
  title,
  dateRange,
  campaignId,
  categoryId
}) => {
  const {
    config: { isDark },
    getThemeColor
  } = useAppContext();
  const [chartData, setChartData] = useState<RiskEvolutionData[]>([]);
  const [viewType, setViewType] = useState<'daily' | 'weekly' | 'monthly'>(
    'daily'
  );
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchRiskEvolution({
          datefrom: dateRange.datefrom,
          dateto: dateRange.dateto,
          campaignid: campaignId,
          interval: viewType
        });
        setChartData(data);
      } catch (error) {
        console.error('Error loading risk evolution data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [dateRange, campaignId, categoryId, viewType]);

  const getChartOption = () => {
    // Transformar datos para Theme River - Incluyendo las 5 severidades
    const themeRiverData: any[] = [];

    chartData.forEach(item => {
      // Critical risks (5)
      if (item.critical > 0) {
        themeRiverData.push([item.date, item.critical, 'Critical']);
      }
      // High risks (4)
      if (item.high > 0) {
        themeRiverData.push([item.date, item.high, 'High']);
      }
      // Medium risks (3)
      if (item.medium > 0) {
        themeRiverData.push([item.date, item.medium, 'Medium']);
      }
      // Low risks (2)
      if (item.low > 0) {
        themeRiverData.push([item.date, item.low, 'Low']);
      }
      // Very Low risks (1)
      const veryLow = item.veryLow || 0;
      if (veryLow > 0) {
        themeRiverData.push([item.date, veryLow, 'Very Low']);
      }
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: 'rgba(0,0,0,0.2)',
            width: 1,
            type: 'solid'
          }
        },
        backgroundColor: !isDark
          ? 'rgba(240, 240, 240, 0.9)'
          : 'rgba(49, 55, 74, 0.9)',
        textStyle: {
          color: !isDark ? '#202020' : '#ccc',
          fontFamily: 'Nunito Sans',
          fontSize: 14
        },
        borderWidth: 1,
        borderColor: !isDark
          ? 'rgba(49, 55, 74, 0.4)'
          : 'rgba(140, 140, 180, 0.8)',
        padding: 10,
        formatter: (params: any) => {
          // Agrupar por fecha
          const dateData: any = {};
          let date = '';

          params.forEach((item: any) => {
            date = item.data[0];
            if (!dateData[item.data[2]]) {
              dateData[item.data[2]] = 0;
            }
            dateData[item.data[2]] = item.data[1];
          });

          let result = `<div style="font-weight: bold; margin-bottom: 8px">${new Date(
            date
          ).toLocaleDateString('en-US', {
            weekday: 'short',
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}</div>`;

          let total = 0;

          const severities = [
            { name: 'Critical', color: '#c00000', level: 5 },
            { name: 'High', color: '#fd7e14', level: 4 },
            { name: 'Medium', color: '#ffc107', level: 3 },
            { name: 'Low', color: '#28a745', level: 2 },
            { name: 'Very Low', color: '#38b755', level: 1 }
          ];

          severities.forEach(({ name, color, level }) => {
            if (dateData[name]) {
              total += dateData[name];
              result += `
                <div style="display: flex; justify-content: space-between; gap: 20px; margin-bottom: 2px;">
                  <span style="display: flex; align-items: center;">
                    <span style="display:inline-block;margin-right:6px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>
                    <span style="font-size: 12px;">${name} (${level})</span>
                  </span>
                  <span style="font-weight: bold; font-size: 13px;">${dateData[name]}</span>
                </div>
              `;
            }
          });

          result += `
            <div style="border-top: 1px solid ${
              !isDark ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'
            }; margin-top: 6px; padding-top: 6px;">
              <div style="display: flex; justify-content: space-between; gap: 20px;">
                <span style="font-weight: 600;">Total Risks</span>
                <span style="font-weight: bold; color: ${getThemeColor(
                  'primary'
                )}">${total}</span>
              </div>
            </div>
          `;

          return result;
        }
      },
      legend: {
        data: ['Very Low', 'Low', 'Medium', 'High', 'Critical'],
        left: 0,
        top: 0,
        itemGap: 20,
        icon: 'circle',
        textStyle: {
          fontFamily: 'Nunito Sans',
          fontSize: 12,
          color: !isDark ? '#666' : '#ccc'
        }
      },
      singleAxis: {
        axisLine: {
          show: false
        },
        left: 20,
        right: 20,
        top: 40,
        bottom: 20,
        axisTick: {
          show: false
        },
        axisLabel: {
          formatter: (value: string) => {
            const date = new Date(value);
            if (viewType === 'monthly') {
              return date.toLocaleDateString('en-US', {
                month: 'short',
                year: 'numeric'
              });
            } else if (viewType === 'weekly') {
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              });
            }
            return date.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric'
            });
          },
          interval: 'auto',
          rotate: chartData.length > 15 ? 45 : 0,
          color: !isDark ? '#666' : '#aaa',
          fontFamily: 'Nunito Sans',
          fontSize: 12
        },
        type: 'time',
        axisPointer: {
          animation: true,
          label: {
            show: false
          }
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            opacity: 0.2,
            color: !isDark ? '#a0a0a0' : '#555'
          }
        }
      },
      series: [
        {
          type: 'themeRiver',
          emphasis: {
            itemStyle: {
              shadowBlur: 20,
              shadowColor: 'rgba(0, 0, 0, 0.6)'
            }
          },
          label: {
            show: false
          },
          itemStyle: {
            color: function (params: any) {
              const colors: any = {
                Critical: '#c00000',
                High: '#fd7e14',
                Medium: '#ffc107',
                Low: '#28a745',
                'Very Low': '#38b755'
              };
              return colors[params.data[2]] || '#999';
            }
          },
          data: themeRiverData
        }
      ],
      animationDuration: 1500,
      animationEasing: 'cubicInOut'
    };
  };

  return (
    <div className="risk-evolution-chart">
      {/* Title and Controls */}
      <div className="d-flex justify-content-between align-items-center mb-1 pe-3">
        <h5 className="mb-0">Evolution: {title}</h5>
        <ButtonGroup size="sm" className="py-1 pe-4">
          <Button
            variant={viewType === 'daily' ? 'primary' : 'phoenix-secondary'}
            onClick={() => setViewType('daily')}
            className="py-1 fs-10"
          >
            Daily
          </Button>
          <Button
            variant={viewType === 'weekly' ? 'primary' : 'phoenix-secondary'}
            onClick={() => setViewType('weekly')}
            className="py-1 fs-10"
          >
            Weekly
          </Button>
          <Button
            variant={viewType === 'monthly' ? 'primary' : 'phoenix-secondary'}
            onClick={() => setViewType('monthly')}
            className="py-1 fs-10"
          >
            Monthly
          </Button>
        </ButtonGroup>
      </div>

      {/* Chart */}
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: '350px' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <ReactEChartsCore
          echarts={echarts}
          option={getChartOption()}
          style={{ height: '350px' }}
          notMerge={true}
          lazyUpdate={true}
        />
      )}
    </div>
  );
};

export default RiskEvolutionChart;
